// Task entity — see FD §5 and §16 (Tasks en triggers).

import type { RiskLevel, RunStatus, TaskSource } from '../constants/index.js'
import type { CustomVars } from '../boards/custom-vars.js'
import type { QueueLane, Severity } from '../boards/types.js'

export interface TaskExternalReference {
  system: 'plane' | 'github' | 'other'
  externalId: string
  /** What the reference points at, e.g. `pull_request`, `branch`, `commit`; null when unknown. */
  refType?: string | null
  url: string | null
}

export const TaskStatus = {
  Pending: 'pending',
  Validating: 'validating',
  Assigned: 'assigned',
  /** The task sits in a non-terminal workflow state (an agent step is running or queued). */
  InProgress: 'in_progress',
  /** The task sits in a human workflow state (a `workflow_gate` approval is pending). */
  AwaitingApproval: 'awaiting_approval',
  /** A workflow task that can't go on by itself (no agent for the role, a failed step, a rejected gate, ...); submitting it again retries its state. */
  Blocked: 'blocked',
  Rejected: 'rejected',
  Cancelled: 'cancelled',
  Completed: 'completed',
} as const

export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus]

export interface Task {
  id: string
  workspaceId: string
  projectId: string
  title: string
  instruction: string
  source: TaskSource
  /** Idempotency key used to dedupe repeated external deliveries (FD §16.3, §26). */
  sourceDedupeKey: string | null
  desiredAgentId: string | null
  desiredRoleId: string | null
  externalReferences: TaskExternalReference[]
  priority: number
  acceptanceCriteria: string[]
  riskLevel: RiskLevel
  maxBudgetCents: number | null
  maxDurationMs: number | null
  desiredSandboxProfileId: string | null
  inputFileRefs: string[]
  requiredApprovalTypes: string[]
  status: TaskStatus
  /** Workflow the task runs through (null: legacy single-run task). */
  workflowKey?: string | null
  /** Current state within `workflowKey`. */
  workflowState?: string | null
  /** Workflow variables set by decision steps (beslisstap contract §2). */
  workflowVars?: import('./decision.js').WorkflowVariables
  /** Model level a decision chose for the rest of the task; null = the agent's own model. */
  modelTier?: import('./workflow.js').ModelTier | null
  /** Task type (e.g. 'feature', 'bug'), from the creator or a Plane label. */
  taskType?: string | null
  /** Git branch every run of this task continues on, set by the first run with a repo. */
  workBranch?: string | null
  /** The task's draft pull request, once a run on `workBranch` has opened or found one. Kept across later runs of the task. */
  pullRequestUrl?: string | null
  /** Boards: the queue lane before the task starts; null once started or without a stream. */
  queueLane?: QueueLane | null
  rank?: string | null
  severity?: Severity | null
  ticketNo?: number | null
  /** 'GOB-190'; null without a ticket number. */
  ticketKey?: string | null
  /** Key of the stream the task belongs to (derived from workflowKey); null when none. */
  streamKey?: string | null
  laneEnteredAt?: string | null
  /** Set when the finished task was archived; archived tasks leave the board and the default list. */
  archivedAt?: string | null
  /** Why the scheduler could not start it (cleared on any manual queue move or edit). */
  queueReason?: string | null
  /** Free key/value map for external systems (source system, external id, url, ...). Goblic does nothing else with it. */
  customVars?: CustomVars
  /** Subtasks (docs/plans/2026-10-03-subtaken.md): the task this one is a subtask of. */
  parentTaskId?: string | null
  /** Subtasks: stable key within the parent, e.g. 'setup'. */
  subtaskKey?: string | null
  /** Subtasks: position within the parent's list. */
  subtaskOrder?: number | null
  /** Parent tasks: counts of their subtasks (null/absent when the task has none). */
  subtaskSummary?: import('./subtasks.js').SubtaskSummary | null
  createdAt: string
  updatedAt: string
}

/**
 * Pre-assignment checks run by the flow engine (FD §16.3). Each field
 * records whether that gate passed, for surfacing in the UI/audit log.
 */
export interface TaskAssignmentCheck {
  hasRequiredData: boolean
  agentIsActive: boolean
  projectPolicyAllows: boolean
  sufficientBudget: boolean
  executorAndWorkerAvailable: boolean
  noDuplicateExternalTaskActive: boolean
}

// ---------------------------------------------------------------------------
// Workflow "steps so far" (task.pullRequestUrl's neighbour: the same ordered
// run history backs both the workflow_gate approval's context and the task
// detail page's workflow-progress stepper — one presenter, two renderers).
// ---------------------------------------------------------------------------

/** One run of a workflow task, in the order it ran. */
export interface TaskRunStep {
  /** `run` (default) or `review`: a person's decision at a workflow gate. For a review, `runId` is the approval's id and there's no run to link to. */
  kind?: 'run' | 'review'
  runId: string
  /** Null for a run outside any workflow (legacy single-run task). */
  workflowState: string | null
  /** How many times the task had already entered `workflowState` before this run (1 = first time) — tells repeated feedback-loop rounds apart. */
  round: number | null
  agentId: string | null
  agentName: string | null
  roleName: string | null
  status: RunStatus
  startedAt: string | null
  endedAt: string | null
  durationMs: number | null
  /** The run's own report (FD §17.3 `summary`), or null before it has one. */
  summary: string | null
  blockers: string[]
  recommendedNextState: string | null
  /**
   * Runs of a handoff chain (checkpoints contract §5) are one step: this
   * step's fields describe the chain's *latest* run, and the earlier runs it
   * took over from are listed here, oldest first ("Claude → GPT-5, usage
   * limit"). Absent or empty when the step is a single run.
   */
  previousAttempts?: TaskRunAttempt[]
  /** Review steps only: the decision, who made it and their note. */
  decision?: 'approved' | 'rejected' | null
  reviewerName?: string | null
  note?: string | null
}

/** An earlier run of a handoff chain, shown inside its step. */
export interface TaskRunAttempt {
  runId: string
  attempt: number
  agentId: string | null
  agentName: string | null
  status: RunStatus
  /** Why it handed off: 'quota' (usage limit) or 'manual'. */
  handoffReason: 'quota' | 'manual' | null
  startedAt: string | null
  endedAt: string | null
}

/** File-level shape of a diff, for a compact "what changed" summary without shipping the full patch. */
export interface DiffstatFile {
  path: string
  insertions: number
  deletions: number
}

export interface Diffstat {
  filesChanged: number
  insertions: number
  deletions: number
  files: DiffstatFile[]
}

/**
 * Everything a `workflow_gate` approval's reviewer needs to judge the work,
 * beyond the bare state transition `ApprovalPanel` already renders: where
 * the code is, what changed, how it got here, and what the last agent is
 * telling the reviewer.
 */
export interface WorkflowGateContext {
  pullRequestUrl: string | null
  branch: string | null
  /** The branch on GitHub (`<repo>/tree/<branch>`); null when the repository isn't on GitHub. */
  branchUrl?: string | null
  /** Every file the task's runs changed, summed over all steps (each run's diff is against its own start); null when there is none (e.g. a Spike task). */
  diffstat: Diffstat | null
  /** Every run of the task so far, oldest first. */
  steps: TaskRunStep[]
  /** The last run before the gate — its summary is the agent's message to the reviewer. Null when the task has no runs yet. */
  handoff: { agentName: string | null; roleName: string | null; summary: string } | null
}
